import { ScreenSizeStore } from '@Components/ScreenSize/ScreenSizeStore';
import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable } from 'inversify';
import { action, computed, observable } from 'mobx';

import container from '../../../../../ioc/ioc';

import { AnalyticsStore } from '~stores/analytics';
import AuthStore from '~stores/authentication';
import SmartBrowserStore from '~stores/smart-browser';
import { ScreenSize } from '~types/views-types';

@injectable()
class SmartBrowserPresenter {
  @inject(AuthStore)
  private AuthStore!: AuthStore;
  @inject(SmartBrowserStore)
  private SmartBrowserStore!: SmartBrowserStore;
  @inject(ScreenSizeStore) private screenSizeStore!: ScreenSizeStore;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;

  @observable slider: boolean = false;
  @observable smartBrowserDialogOpen: boolean = false;
  @observable turnOffDialogOpen: boolean = false;
  @observable tellMoreDialogOpen: boolean = false;
  @observable emailAddress: string = '';
  @observable password: string = '';
  @observable confirmPassword: string = '';
  @observable shouldShowTurnOffMessage: boolean = true;
  @observable deactivateCheckbox: boolean = false;
  @observable isSignUpMode: boolean = false;
  @observable authError: string | null = null;

  @action public setSignUpMode = (setting: boolean) => {
    this.isSignUpMode = setting;

    this.authError = null;
    this.emailAddress = '';
    this.password = '';
    this.confirmPassword = '';
  };

  @action public toggleDeactivateCheckbox = () => {
    this.deactivateCheckbox = !this.deactivateCheckbox;
  };

  @action public checkSmartBrowserStatus = async () => {
    if (this.SmartBrowserStore.savedPreferences?.smartBrowsingActive) {
      this.slider = true;
    }
    if (this.SmartBrowserStore.savedPreferences) {
      this.shouldShowTurnOffMessage =
        this.SmartBrowserStore.savedPreferences?.smartBrowsingDeactivationWarning;
    }
  };

  @action openSmartBrowser = () => {
    this.smartBrowserDialogOpen = true;
  };

  @action public toggleSmartBrowser = async (
    turnOn: boolean,
    shouldDeactivate?: boolean
  ) => {
    if (!this.SmartBrowserStore.savedPreferences) {
      this.slider = turnOn;
      await this.SmartBrowserStore.setUserSmartBrowsing(turnOn, false);
      return;
    }
    const { smartBrowsingDeactivationWarning } =
      this.SmartBrowserStore.savedPreferences;

    if (!turnOn && shouldDeactivate) {
      await this.SmartBrowserStore.setUserSmartBrowsing(false, true);
      this.slider = turnOn;
      return;
    }

    if (!turnOn && smartBrowsingDeactivationWarning) {
      await this.SmartBrowserStore.setUserSmartBrowsing(false, true);
      this.slider = turnOn;
      return;
    }

    if (turnOn && smartBrowsingDeactivationWarning) {
      await this.SmartBrowserStore.setUserSmartBrowsing(true, true);
      this.slider = turnOn;
      return;
    }

    if (turnOn && !smartBrowsingDeactivationWarning) {
      await this.SmartBrowserStore.setUserSmartBrowsing(true, false);
      this.slider = turnOn;
      return;
    }

    await this.SmartBrowserStore.setUserSmartBrowsing(turnOn, false);
    this.slider = turnOn;
  };

  @action public handleSwitchPress = async (turnOn: boolean) => {
    if (!this.AuthStore.currentUser) {
      this.slider = turnOn;
      this.smartBrowserDialogOpen = true;
      return;
    }
    if (!this.SmartBrowserStore.savedPreferences) {
      this.turnOffDialogOpen = true;
      return;
    }

    const { smartBrowsingDeactivationWarning } =
      this.SmartBrowserStore.savedPreferences;

    if (!smartBrowsingDeactivationWarning && !turnOn) {
      this.turnOffDialogOpen = true;
      return;
    }

    this.toggleSmartBrowser(turnOn).then();
  };

  @action public handleTurnOffPress = async () => {
    if (this.deactivateCheckbox) {
      this.toggleSmartBrowser(false, true).then();
      this.turnOffDialogOpen = false;
      return;
    }
    this.toggleSmartBrowser(false).then();
    this.turnOffDialogOpen = false;
  };

  @action public setEmailAddress = (value: string) => {
    this.emailAddress = value;
  };

  @action public setPassword = (value: string) => {
    this.password = value;
  };

  @action public setConfirmPassword = (value: string) => {
    this.confirmPassword = value;
  };

  @action public setSlider = (setting: boolean) => {
    this.slider = setting;

    if (setting) {
      this.smartBrowserDialogOpen = true;
    }
  };

  @action public setTellMoreModal = (setting: boolean) => {
    this.tellMoreDialogOpen = setting;

    if (setting && this.smartBrowserDialogOpen) {
      this.smartBrowserDialogOpen = false;
      this.slider = false;
    }
  };

  @action handleTellMeMorePress = () => {
    this.setTellMoreModal(true);
  };

  @action public handleCloseModal = () => {
    this.smartBrowserDialogOpen = false;
    this.slider = false;
    this.isSignUpMode = false;
    this.authError = null;
    this.setPassword('');
    this.setEmailAddress('');
  };

  @action public handleCloseTellMoreModal = () => {
    this.tellMoreDialogOpen = false;
  };

  @action public handleCloseTurnOffModal = () => {
    this.turnOffDialogOpen = false;
    this.slider = true;
  };

  @action public handleLoginSubmit = async () => {
    const signInResponse = await this.AuthStore.authenticateAsync(
      this.emailAddress,
      this.password
    );

    // For some reason, serviceStack is returning `TypeError: Right hand side of instanceof is not an object` when auth details are incorrect
    // As a workaround, we'll check if the result is falsy and show an error message if it is
    if (!signInResponse) {
      this.authError = 'The email address or password is incorrect.';
      return;
    }

    if (signInResponse.success) {
      this.AuthStore.currentUser = signInResponse;
      this.toggleSmartBrowser(true).then();
      this.smartBrowserDialogOpen = false;
      this.setPassword('');
      this.setEmailAddress('');

      this._analyticsStore.logLogin({
        userId: this.AuthStore.currentUser?.id!,
        context: 'login',
      });
      return;
    }

    this.authError = signInResponse.message;
  };

  @action public handleSignupSubmit = async () => {
    const signUpResponse = await this.AuthStore.signUp({
      confirmPassword: this.confirmPassword,
      emailAddress: this.emailAddress,
      password: this.password,
    });

    if (signUpResponse.success) {
      const signInResponse = await this.AuthStore.authenticateAsync(
        this.emailAddress,
        this.password
      );

      this._analyticsStore.logSignupSubmit({ method: 'login' });

      if (signInResponse.success) {
        this.AuthStore.currentUser = signInResponse;
        this.toggleSmartBrowser(true).then();
        this.smartBrowserDialogOpen = false;
        this.setPassword('');
        this.setEmailAddress('');
        return;
      }

      this.authError = signInResponse.message;
    }

    this.authError = signUpResponse.errors[0].userMessage;
  };

  @computed get isNotMobileScreen() {
    return this.screenSizeStore.size !== ScreenSize.mobile;
  }

  @computed get screenSize() {
    return this.screenSizeStore.size;
  }
}

const useSmartBrowserPresenter = () =>
  useClassStore<SmartBrowserPresenter>(container.get(SmartBrowserPresenter));

export default useSmartBrowserPresenter;
